import * as types from '../mutation-types'
import { Api } from '@/api/api'
import { DateTime } from 'luxon'
import { debounce } from 'debounce'
import { getCheckoutData } from '@/utils/bookingData'
import { getJsDate, intervalsOverlap, interval } from '@/utils/date'
import { times, isEmpty } from '@/utils/general'
import { cloneDeep } from 'lodash'
import { router } from '@/router'

const debouncedGetAvailabilities = debounce(function(dispatch) {
  /* istanbul ignore next */
  dispatch('campground/getAvailabilities', null, { root: true })
}, 200)

const debouncedCheckPrice = debounce(function(dispatch) {
  /* istanbul ignore next */
  dispatch('campground/checkPrice', null, { root: true })
}, 200)

const getBillingFromBookingDetails = confirmation => {
  return {
    city: confirmation.city,
    country: { code: confirmation.country },
    lastname: confirmation.last_name,
    mail: confirmation.email,
    message: confirmation.message,
    name: confirmation.first_name,
    phone: confirmation.phone,
    street: confirmation.street1,
    street_additional: confirmation.street2,
    zip: confirmation.zip_code,
  }
}

// initial state
export const initialState = () => {
  return {
    aid: null,
    billing: {},
    car: {},
    checkout: {
      data: {},
      responseAdyen: {},
      responseCheckout: {},
      status: {
        error: false,
        loading: false,
        success: false,
      },
    },
    confirmation: {},
    dates: [],
    displayedDate: null,
    isCompleted: false,
    price: {
      subtotal: 0,
      total: 0,
      currency_alt: { total: 0 },
    },
    selectedCampsite: null,
    selectedExtras: [],
    selectedPersons: [],
    selectedSeasons: [],
    token: null,
    travellers: {},
    oldBookingId: null,
  }
}
let state = initialState()

// getters
const getters = {
  aid: state => state.aid,
  billing: state =>
    state.isCompleted ? getBillingFromBookingDetails(state.confirmation) : state.billing,
  car: state =>
    state.isCompleted
      ? {
          length: state.confirmation.vehicle_length,
          license_plate: state.confirmation.license_plate,
          width: state.confirmation.vehicle_width,
        }
      : state.car,
  checkout: state => state.checkout,
  confirmation: state => state.confirmation,
  dates: state =>
    state.isCompleted
      ? [state.confirmation.booking_data.date_from, state.confirmation.booking_data.date_until]
      : state.dates,
  displayedDate: state => getJsDate(state.displayedDate),
  isCompleted: state => state.isCompleted,
  price: state => {
    if (state.isCompleted) {
      return state.confirmation.pricelist
    }
    return state.price
  },
  selectedCampsite: state => state.selectedCampsite,
  selectedExtras: state => state.selectedExtras,
  selectedPersons: state => state.selectedPersons,
  selectedSeasons: state => state.selectedSeasons,
  status: state => state.status,
  token: state => state.token,
  travellers: state => (state.isCompleted ? state.confirmation.guests : state.travellers),
}

// actions
const actions = {
  handleStoredCheckout({ commit, state, rootGetters }) {
    /* reset adyen response */
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseAdyen',
      value: {},
    })

    const checkoutData = getCheckoutData({
      aid: state.aid,
      billing: state.billing,
      car: state.car,
      dates: state.dates,
      selectedCampsite: state.selectedCampsite,
      selectedExtras: state.selectedExtras,
      selectedPersons: state.selectedPersons,
      travellers: state.travellers,
    })

    if (checkoutData.aid === null) checkoutData.aid = 'e4f1afde-65cd-432f-b38e-288af3919e93'

    const oldCheckoutData = state.checkout.data

    /* check if there are any checkoutData or checkoutData have changed */
    if (
      !isEmpty(oldCheckoutData) &&
      JSON.stringify(checkoutData) !== JSON.stringify(oldCheckoutData)
    ) {
      /* delete pending booking - result of call doesn't matter to FE */
      const cancelBookingUrl = rootGetters['campground/urls'].cancel_pending_booking
      const bookingId = state.checkout.responseCheckout.booking_id
      Api.getData(`${cancelBookingUrl}?booking_id=${bookingId}`)

      /* reset checkoutData & responseCheckout */
      commit(types.BOOKING_SET_CHECKOUT, {
        key: 'data',
        value: {},
      })
      commit(types.BOOKING_SET_CHECKOUT, {
        key: 'responseCheckout',
        value: {},
      })
    }
  },
  getBookingDetails({ rootGetters, dispatch }, bookingId) {
    const bookingDetailsUrl = rootGetters['campground/urls'].booking_details
    const campgroundSlug = rootGetters['campground/campground'].slug
    return Api.getData(`${bookingDetailsUrl}${campgroundSlug}/${bookingId}/`)
      .then(result => {
        // console.log('result:', result)
        /* set confirmation data and completed state */
        dispatch('setBookingDetails', result)
        return result
      })
      .catch(e => {
        throw e
      })
  },
  setBookingDetails({ commit, dispatch }, data) {
    commit(types.BOOKING_SET_CONFIRMATION, data)
    commit(types.BOOKING_SET_COMPLETED, true)

    if (data.aid) {
      commit(types.BOOKING_SET_AID, data.aid)
    }

    /* set currency based confirmation data */
    dispatch('application/setCurrency', { code: data.pricelist.currency }, { root: true })
  },
  setAid({ commit }, string) {
    commit(types.BOOKING_SET_AID, string)
  },
  setBilling({ commit }, { k, v }) {
    commit(types.BOOKING_SET_BILLING, { k, v })
  },
  setCar({ commit }, { k, v }) {
    commit(types.BOOKING_SET_CAR, { k, v })
  },
  setCompleted({ commit }, value) {
    commit(types.BOOKING_SET_COMPLETED, value)
  },
  setRequestedTravelData({ rootGetters, dispatch }, { acco, adults, children }) {
    let selectedCampsite = null
    let selectedAdult = null
    let selectedChild = null

    // if children are requested, we want to skip automatic selection of guests,
    // because it's unclear how the campsite defines children and adults.
    // if (children > 0) {
    //   children = 0
    //   adults = 0
    // }

    if (acco) {
      const availableCampsites = rootGetters['campground/campground'].campsites
      selectedCampsite = availableCampsites.filter(c => c.id === acco)
      if (selectedCampsite.length) {
        selectedCampsite = cloneDeep(selectedCampsite[0])
        selectedCampsite.amount = 1
      } else {
        selectedCampsite = null
      }
    }

    const seasons = rootGetters['booking/selectedSeasons']

    let availableChildren = cloneDeep(
      rootGetters['campground/campground'].persons.filter(person => person.person_type === 'child')
    )
    let availableAdults = cloneDeep(
      rootGetters['campground/campground'].persons.filter(person => person.person_type === 'adult')
    )
    if (seasons && seasons.length) {
      let season_ids = []
      seasons.forEach(season => {
        season_ids.push(season.id)
      })

      const getMinPrice = prices => {
        let filteredPrices = prices.filter(price => {
          if (season_ids.includes(price.season)) {
            if (selectedCampsite) {
              return selectedCampsite.campsite_type === price.campsite_type
            } else {
              return true
            }
          } else {
            return false
          }
        })
        if (!filteredPrices || !filteredPrices.length) {
          return 0
        }
        return filteredPrices.sort(
          (p1, p2) => (p1.value < p2.value ? -1 : p1.value > p2.value ? 1 : 0)
        )[0].value
      }

      if (availableAdults && availableAdults.length) {
        availableAdults.forEach(adult => {
          adult.minPrice = getMinPrice(adult.prices)
        })
        availableAdults = availableAdults.sort(
          (p1, p2) => (p1.minPrice < p2.minPrice ? -1 : p1.minPrice > p2.minPrice ? 1 : 0)
        )
      }
      if (availableChildren && availableChildren.length) {
        availableChildren.forEach(child => {
          child.minPrice = getMinPrice(child.prices)
        })
        availableChildren = availableChildren.sort(
          (p1, p2) => (p1.minPrice < p2.minPrice ? -1 : p1.minPrice > p2.minPrice ? 1 : 0)
        )
      }
    }

    // If there are two or more adults defined, we do not want to set selectedAdult
    if (availableAdults.length) {
      if (adults) {
        selectedAdult = availableAdults[0]
      }
    }

    // If there are two or more adults defined, we do not want to set selectedChild
    if (availableChildren.length) {
      if (children) {
        selectedChild = availableChildren[0]
      }
    }

    let step1fullfilled = false

    if (children && selectedChild) {
      dispatch('setPerson', {
        amount: children,
        person: selectedChild,
      })
    }
    if (adults && selectedAdult) {
      dispatch('setPerson', {
        amount: adults,
        person: selectedAdult,
      })
      dispatch(
        'application/setStepStatus',
        { number: 1, key: 'fulfilled', value: true },
        { root: true }
      )
      step1fullfilled = !children || (children && selectedChild)
    }
    if (selectedCampsite) {
      dispatch(
        'application/setStepStatus',
        { number: 2, key: 'selectedCampsiteType', value: selectedCampsite.campsite_type },
        { root: true }
      )
      dispatch('setSelectedCampsite', selectedCampsite)
      if (step1fullfilled) {
        let params = {}
        if (router.currentRoute && router.currentRoute.params) {
          params = router.currentRoute.params
        }
        setTimeout(() => {
          router.push({
            name: 'Step',
            params: { ...params, step: 2 },
          })
        }, 10)
      }
    }
  },
  setDates({ commit, dispatch, state, rootGetters }, dates) {
    commit(types.BOOKING_SET_DATES, dates)

    let seasonIds = []
    let chosenSeasons = []
    let minimumNights = {}

    /* istanbul ignore else */
    if (dates && dates.length === 2 && dates[1] !== null && dates[0] !== null) {
      const dateStart = getJsDate(dates[0])
      const dateEnd = DateTime.fromJSDate(getJsDate(dates[1])).minus({ days: 1 })
      const bookingRange = interval(dateStart, dateEnd)
      const seasons = rootGetters['campground/campground'].seasons

      /* Seasons are not longer supported/needed */
      if (seasons.length > 2) {
        seasons.forEach(season => {
          season.time_periods.forEach(tp => {
            let r = interval(tp.date_start, tp.date_end)

            /* istanbul ignore else */
            if (intervalsOverlap(bookingRange, r, false, true)) {
              /* istanbul ignore else */
              if (!seasonIds.includes(season.id)) {
                seasonIds.push(season.id)
                chosenSeasons.push(season)
              }
              /* istanbul ignore else */
              if (r.contains(DateTime.fromJSDate(dateStart)) && !season.is_lock) {
                for (let night of season.minimum_nights) {
                  /* istanbul ignore else */
                  if (night.amount > 0) {
                    minimumNights[night.campsite_type] = night.amount
                  }
                }
              }
            }
          })
        })
        commit(types.BOOKING_SET_SELECTED_SEASONS, chosenSeasons)
      }
      if (state.selectedPersons.length > 0) {
        dispatch('campground/getAvailabilities', null, { root: true })
      }
    }
    dispatch('campground/checkPrice', null, { root: true })
  },
  setDisplayedDate({ commit }, displayedDate) {
    commit(types.BOOKING_SET_DISPLAYED_DATE, displayedDate)
  },
  setSelectedCampsite({ commit, dispatch, state, rootGetters }, campsite) {
    const extras = rootGetters['campground/campground'].extras
    commit(types.BOOKING_SET_SELECTED_CAMPSITE, { campsite, extras })
    if (state.selectedCampsite !== null) {
      dispatch(
        'application/setStepStatus',
        {
          number: 2,
          key: 'fulfilled',
          value: true,
        },
        { root: true }
      )
    } else {
      dispatch(
        'application/setStepStatus',
        {
          number: 2,
          key: 'fulfilled',
          value: false,
        },
        { root: true }
      )
    }
    dispatch('campground/checkPrice', null, { root: true })
  },
  setPerson({ commit, dispatch, state, rootGetters }, { amount, person }) {
    const campgroundPersons = rootGetters['campground/campground'].persons
    commit(types.BOOKING_SET_PERSON, { amount, person, campgroundPersons })

    if (state.selectedPersons.length > 0) {
      debouncedGetAvailabilities(dispatch)
    }
    debouncedCheckPrice(dispatch)
  },
  setExtra({ commit, dispatch }, { extra, amount }) {
    commit(types.BOOKING_SET_EXTRA, { extra, amount })
    debouncedCheckPrice(dispatch)
  },
  setCheckout({ commit }, { key, value }) {
    commit(types.BOOKING_SET_CHECKOUT, { key, value })
  },
  setTraveller({ commit }, { k, i, v }) {
    commit(types.BOOKING_SET_TRAVELLER, { k, i, v })
  },
  setToken({ commit }, string) {
    commit(types.BOOKING_SET_TOKEN, string)
  },
  setPrice({ commit }, price) {
    commit(types.BOOKING_SET_PRICE, price)
  },
  resetAdyen({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseAdyen',
      value: {},
    })
  },
  resetCheckout({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'responseCheckout',
      value: {},
    })
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.BOOKING_RESET_STATE, initState)
  },
  resetStatus({ commit }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'status',
      value: { loading: false, error: false, success: false },
    })
  },
  performCheckout({ commit, dispatch, state, rootGetters }) {
    commit(types.BOOKING_SET_CHECKOUT, {
      key: 'status',
      value: { loading: true, error: false, success: false },
    })

    const bookingData = getCheckoutData({
      aid: state.aid,
      billing: state.billing,
      car: state.car,
      dates: state.dates,
      selectedCampsite: state.selectedCampsite,
      selectedExtras: state.selectedExtras,
      selectedPersons: state.selectedPersons,
      travellers: state.travellers,
    })

    bookingData.old_booking_id = state.oldBookingId

    if (bookingData.aid === null) bookingData.aid = 'e4f1afde-65cd-432f-b38e-288af3919e93'

    return Api.postData(rootGetters['campground/urls'].checkout, bookingData)
      .then(result => {
        // add created date to result
        result.created = new Date()

        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'data',
          value: bookingData,
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'responseCheckout',
          value: result,
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'status',
          value: { loading: false, error: false, success: true },
        })
        dispatch(
          'application/setStepStatus',
          { number: 2, key: 'selectedCampsiteIsAvailable', value: true },
          { root: true }
        )
        commit(types.BOOKING_SET_OLD_BOOKING_ID, result.booking_id)
        return result
      })
      .catch(e => {
        if (!e.response) e.response = 'unknown error'
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'status',
          value: { loading: false, error: true, success: false },
        })
        commit(types.BOOKING_SET_CHECKOUT, {
          key: 'responseCheckout',
          value: e.response,
        })
        throw e
      })
  },
  prepareBookingForPayment({ rootGetters }, { bookingId }) {
    const url = `${rootGetters['campground/urls'].prepare_booking}?booking_id=${bookingId}`
    return Api.getData(url).then(result => {
      return result
    })
  },
  processPayload({ rootGetters }, { id, payload }) {
    const data = {
      payload: payload,
      payment: id,
      testmode: rootGetters['campground/campground'].testmode,
    }

    return Api.postData(rootGetters['campground/urls'].payload, data)
      .then(result => {
        return result
      })
      .catch(e => {
        throw e
      })
  },
  resetTimer({ commit }) {
    commit(types.BOOKING_RESET_TIMER)
  },
}

// mutations
const mutations = {
  [types.BOOKING_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      // Always keep existing Affiliate ID!
      if (key !== 'aid') {
        state[key] = initState[key]
      }
    })
  },
  [types.BOOKING_SET_AID](state, payload) {
    state.aid = payload
  },
  [types.BOOKING_SET_BILLING](state, { k, v }) {
    state.billing = { ...state.billing, [k]: v }
  },
  [types.BOOKING_SET_CAR](state, { k, v }) {
    state.car = { ...state.car, [k]: v }
  },
  [types.BOOKING_SET_CHECKOUT](state, { key, value }) {
    state.checkout = { ...state.checkout, [key]: value }
  },
  [types.BOOKING_SET_CONFIRMATION](state, result) {
    state.confirmation = result
  },
  [types.BOOKING_SET_DATES](state, payload) {
    state.dates = payload
  },
  [types.BOOKING_SET_DISPLAYED_DATE](state, payload) {
    state.displayedDate = payload
  },
  [types.BOOKING_SET_EXTRA](state, { extra, amount }) {
    const old = state.selectedExtras.find(p => p.id === extra.id)

    // add value
    if (!old) state.selectedExtras = [...state.selectedExtras, { id: extra.id, amount: +amount }]
    // remove value
    if (!amount || amount === 0) {
      state.selectedExtras = state.selectedExtras.filter(e => e.id !== extra.id)
    } else {
      // update value
      state.selectedExtras = state.selectedExtras.map(ex => {
        if (ex.id === extra.id) return Object.assign({}, ex, { id: extra.id, amount: +amount })
        return ex
      })
    }
  },
  [types.BOOKING_SET_COMPLETED](state, value) {
    state.isCompleted = value
  },
  [types.BOOKING_SET_PERSON](state, { amount, person, campgroundPersons }) {
    const old = state.selectedPersons.find(p => p.id === person.id)

    // add value
    if (!old) state.selectedPersons = [...state.selectedPersons, { id: person.id, amount: +amount }]
    // remove value
    if (!amount || amount === 0) {
      state.selectedPersons = state.selectedPersons.filter(p => p.id !== person.id)
    } else {
      // update value
      state.selectedPersons = state.selectedPersons.map(p => {
        if (p.id === person.id) return Object.assign({}, p, { id: person.id, amount: +amount })
        return p
      })
    }

    // for every person and an traveller object to the travellers array
    state.travellers = []
    state.selectedPersons.forEach(person => {
      const p = campgroundPersons.find(p => p.id === person.id)
      /* istanbul ignore else */
      if (p && p.person_type !== 'pet') {
        times(person.amount)(() => {
          state.travellers.push({})
        })
      }
    })
  },
  [types.BOOKING_SET_SELECTED_CAMPSITE](state, { campsite, extras }) {
    state.selectedCampsite = campsite === state.selectedCampsite ? null : campsite

    // filter selected extras if they are valid for new selected campsite type
    /* istanbul ignore else */
    if (state.selectedCampsite !== null) {
      state.selectedExtras = state.selectedExtras.filter(extra => {
        const basicExtra = extras.find(ex => ex.id === extra.id)
        return basicExtra[`applies_for_${state.selectedCampsite.campsite_type}`]
      })
    }
  },
  [types.BOOKING_SET_SELECTED_SEASONS](state, payload) {
    console.log('BOOKING_SET_SELECTED_SEASONS', state, payload)
    state.selectedSeasons = payload
  },
  [types.BOOKING_SET_PRICE](state, payload) {
    state.price = payload
  },
  [types.BOOKING_SET_TRAVELLER](state, { k, i, v }) {
    const traveller = { ...state.travellers[i], [k]: v }
    state.travellers = { ...state.travellers, [i]: traveller }
  },
  [types.BOOKING_SET_TOKEN](state, payload) {
    state.token = payload
    Api.setToken(payload)
  },
  [types.BOOKING_RESET_TIMER](state) {
    state.checkout.responseCheckout = state.checkout.responseCheckout || {}
    state.checkout.responseCheckout.created = new Date()
  },
  [types.BOOKING_SET_OLD_BOOKING_ID](state, bookingId) {
    state.oldBookingId = bookingId
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
